import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Search, Download, AlertCircle, FileText, ArrowRightLeft, Check, CheckCircle2, ArrowUpDown, X, PenTool, Eye, Printer, Camera, QrCode, Clock } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/utils/dates";
import SignatureFlow from "@/components/SignatureFlow";
import { QRCodeSVG } from "qrcode.react";

const paymentCfg = {
  da_pagare: { label: "Da Pagare", cls: "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/50 dark:text-red-200 dark:border-red-700" },
  acconto: { label: "Acconto", cls: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/50 dark:text-amber-200 dark:border-amber-700" },
  pagato: { label: "Pagato", cls: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/50 dark:text-emerald-200 dark:border-emerald-700" },
};
const statusCfg = {
  prenotazione: { label: "Prenotazione", cls: "bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900/50 dark:text-indigo-200 dark:border-indigo-700" },
  attivo: { label: "Attivo", cls: "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/50 dark:text-yellow-200 dark:border-yellow-700" },
  completato: { label: "Completato", cls: "bg-slate-200 text-slate-700 border-slate-400 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-500" },
  annullato: { label: "Annullato", cls: "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/50 dark:text-red-200 dark:border-red-700" },
};

const emptyForm = { vehicle_id: "", client_id: "", start_date: "", end_date: "", start_time: "09:00", end_time: "09:00", daily_rate: 0, deposit_amount: 0, payment_status: "da_pagare", status: "prenotazione", notes: "", home_delivery: false, home_delivery_fee: 40, extra_day: false, extra_day_amount: 0, discount_amount: 0, second_driver_id: "", second_driver_fee: 0 };

export default function RentalsPage() {
  const { axiosAuth } = useAuth();
  const { settings } = useSettings();
  const accent = settings?.primary_color || "#DC2626";
  const [rentals, setRentals] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [activeTab, setActiveTab] = useState("tutti");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [overlapWarning, setOverlapWarning] = useState(null);
  const [creatingContract, setCreatingContract] = useState(null);
  const [editingDeposit, setEditingDeposit] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [hideCompleted, setHideCompleted] = useState(true); // { rentalId, photo } // { url, name }
  const [depositValue, setDepositValue] = useState("");
  // Client search
  const [clientSearch, setClientSearch] = useState("");
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const clientRef = useRef(null);
  // Second driver search
  const [sdSearch, setSdSearch] = useState("");
  const [sdDropdownOpen, setSdDropdownOpen] = useState(false);
  const sdRef = useRef(null);
  // Completion dialog
  const [completionDialog, setCompletionDialog] = useState(null);
  const [signatureFlow, setSignatureFlow] = useState(null);
  const [signatureChoice, setSignatureChoice] = useState(null);
  const [signedDialog, setSignedDialog] = useState(null);
  const [signDestDialog, setSignDestDialog] = useState(null); // { rental, client, vehicle }
  const [completionForm, setCompletionForm] = useState({ actual_return_date: "", actual_return_time: "09:00", actual_days: 1 });
  const [completionDone, setCompletionDone] = useState(false);
  const [completionSubmitting, setCompletionSubmitting] = useState(false);
  const [qrDialog, setQrDialog] = useState(null); // { booking_code, client_name, vehicle_name }
  const [timeDialog, setTimeDialog] = useState(null); // { id, contract_time }
  const [timeValue, setTimeValue] = useState("");
  const [pendingKiosk, setPendingKiosk] = useState([]);

  const fetchPendingKiosk = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/signatures/kiosk/pending");
      setPendingKiosk(Array.isArray(res.data) ? res.data : []);
    } catch {}
  }, [axiosAuth]);

  useEffect(() => {
    fetchPendingKiosk();
    const iv = setInterval(fetchPendingKiosk, 15000);
    return () => clearInterval(iv);
  }, [fetchPendingKiosk]);

  const cancelPendingKiosk = async (target) => {
    try {
      await axiosAuth().post("/signatures/kiosk/cancel", { target });
      toast.success("Richiesta firma annullata");
      fetchPendingKiosk();
    } catch { toast.error("Errore annullamento"); }
  };

  const fetchData = useCallback(async () => {
    try {
      const api = axiosAuth();
      const [rRes, vRes, cRes] = await Promise.all([api.get("/rentals"), api.get("/vehicles"), api.get("/clients")]);
      setRentals(rRes.data);
      setVehicles(vRes.data);
      setClients(cRes.data);
    } catch { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Close client dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (clientRef.current && !clientRef.current.contains(e.target)) setClientDropdownOpen(false);
      if (sdRef.current && !sdRef.current.contains(e.target)) setSdDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSave = async () => {
    setSubmitting(true);
    setOverlapWarning(null);
    try {
      const api = axiosAuth();
      if (editing) {
        await api.put(`/rentals/${editing.id}`, {
          vehicle_id: form.vehicle_id, client_id: form.client_id, start_date: form.start_date, end_date: form.end_date,
          start_time: form.start_time, end_time: form.end_time, daily_rate: parseFloat(String(form.daily_rate).replace(",", ".")) || 0,
          deposit_amount: parseFloat(String(form.deposit_amount).replace(",", ".")) || 0, payment_status: form.payment_status,
          status: form.status, notes: form.notes,
          home_delivery: form.home_delivery, home_delivery_fee: parseFloat(String(form.home_delivery_fee).replace(",", ".")) || 0,
          extra_day: form.extra_day, extra_day_amount: parseFloat(String(form.extra_day_amount).replace(",", ".")) || 0,
          discount_amount: parseFloat(String(form.discount_amount).replace(",", ".")) || 0,
          second_driver_id: form.second_driver_id || "", second_driver_fee: parseFloat(String(form.second_driver_fee).replace(",", ".")) || 0
        });
        toast.success("Noleggio aggiornato");
      } else {
        await api.post("/rentals", { ...form, daily_rate: parseFloat(String(form.daily_rate).replace(",", ".")) || 0, deposit_amount: parseFloat(String(form.deposit_amount).replace(",", ".")) || 0, home_delivery_fee: parseFloat(String(form.home_delivery_fee).replace(",", ".")) || 0, extra_day_amount: parseFloat(String(form.extra_day_amount).replace(",", ".")) || 0, discount_amount: parseFloat(String(form.discount_amount).replace(",", ".")) || 0, second_driver_fee: parseFloat(String(form.second_driver_fee).replace(",", ".")) || 0 });
        toast.success("Noleggio creato");
      }
      setDialogOpen(false); setEditing(null); fetchData();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Errore";
      if (status === 409) { setOverlapWarning(detail); toast.error("Sovrapposizione rilevata! Cambia le date."); }
      else { toast.error(detail); }
    }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    try { const api = axiosAuth(); await api.delete(`/rentals/${deleteDialog.id}`); toast.success("Eliminato"); setDeleteDialog(null); fetchData(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const downloadContract = async (rid) => {
    try {
      const api = axiosAuth();
      const res = await api.get(`/rentals/${rid}/contract`, { responseType: "blob" });
      const r = rentals.find(x => x.id === rid);
      const cl = r ? clients.find(c => c.id === r.client_id) : null;
      const vh = r ? vehicles.find(v => v.id === r.vehicle_id) : null;
      const cn = (r?.contract_number || "").replace("/", "-");
      const surname = (cl?.surname || "").toUpperCase();
      const vName = `${vh?.brand || ""} ${vh?.model || ""}`.trim().toUpperCase();
      const fname = [cn, surname, vName].filter(Boolean).join(" ") || `contratto_${rid.slice(0, 8)}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${fname}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Contratto scaricato!");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore download"); }
  };

  const previewContract = async (rid, contractNum) => {
    try {
      const api = axiosAuth();
      const res = await api.get(`/rentals/${rid}/contract`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPdfPreview({ url, name: `Contratto ${contractNum || ""}`.trim(), rid });
    } catch (err) { toast.error(err.response?.data?.detail || "Errore apertura anteprima"); }
  };

  const printPreview = () => {
    if (!pdfPreview?.url) return;
    const iframe = document.getElementById("pdf-preview-iframe");
    if (iframe) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  const closePdfPreview = () => {
    if (pdfPreview?.url) window.URL.revokeObjectURL(pdfPreview.url);
    setPdfPreview(null);
  };

  const deletePickupPhoto = async (rid) => {
    try {
      const api = axiosAuth();
      await api.delete(`/kiosk/photo/${rid}`);
      toast.success("Foto eliminata");
      setPhotoPreview(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore eliminazione foto"); }
  };

  const createContract = async (rid) => {
    setCreatingContract(rid);
    try {
      const api = axiosAuth();
      const res = await api.post(`/rentals/${rid}/create-contract`);
      toast.success(`Contratto #${res.data.contract_number} creato! Stato: Attivo`);
      fetchData();
      previewContract(rid, res.data.contract_number);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore creazione contratto"); }
    finally { setCreatingContract(null); }
  };

  const deleteContract = async (rid) => {
    if (!window.confirm("Eliminare il contratto? Il numero contratto verra' recuperato.")) return;
    try {
      const api = axiosAuth();
      await api.delete(`/rentals/${rid}/contract`);
      toast.success("Contratto eliminato, numero recuperato!");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore eliminazione contratto"); }
  };

  const openSignatureFlow = (r) => {
    // If already signed, show options dialog
    if (r.signed) {
      setSignedDialog(r);
      return;
    }
    const client = clients.find(c => c.id === r.client_id);
    const vehicle = vehicles.find(v => v.id === r.vehicle_id);
    // Always show destination choice dialog first
    setSignDestDialog({ rental: r, client, vehicle });
  };

  const removeSignature = async (rid) => {
    try {
      const api = axiosAuth();
      await api.put(`/rentals/${rid}`, { status: "attivo" });
      await api.post("/signatures/remove-signature", { rental_id: rid });
      toast.success("Firma rimossa dal contratto");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore rimozione firma"); }
    setSignedDialog(null);
  };

  const resignContract = (r) => {
    setSignedDialog(null);
    const client = clients.find(c => c.id === r.client_id);
    const vehicle = vehicles.find(v => v.id === r.vehicle_id);
    setSignDestDialog({ rental: r, client, vehicle });
  };

  const sendToKiosk = async (r, target = "tablet") => {
    try {
      const api = axiosAuth();
      await api.post("/signatures/kiosk/request", { rental_id: r.id, target });
      const isTablet = target === "tablet";
      toast.info(isTablet ? "Richiesta firma inviata al tablet!" : "Richiesta firma inviata al Kiosk Self-Service!", {
        duration: 600000,
        description: isTablet ? "In attesa della firma del cliente..." : "Il cliente puo' completare la firma dal chiosco...",
        action: {
          label: "Annulla",
          onClick: async () => {
            try {
              await api.post("/signatures/kiosk/cancel");
              toast.success("Richiesta firma annullata");
            } catch {}
          },
        },
      });
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const res = await api.get(`/signatures/kiosk/check-result/${r.id}`);
          if (res.data.status === "completed") {
            clearInterval(poll);
            toast.dismiss();
            toast.success(isTablet ? "Contratto firmato dal tablet!" : "Contratto firmato dal Kiosk!");
            fetchData();
          }
        } catch {}
      }, 3000);
      // Timeout after 10 minutes
      setTimeout(() => { clearInterval(poll); toast.dismiss(); }, 600000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio richiesta firma");
    }
  };

  const useRegisteredSignature = async () => {
    if (!signatureChoice) return;
    const { rental, client } = signatureChoice;
    try {
      const api = axiosAuth();
      const sigRes = await api.get(`/signatures/${client.id}`);
      const savedSig = sigRes.data?.signatures?.[0];
      if (!savedSig) { toast.error("Nessuna firma registrata per questo cliente"); setSignatureChoice(null); return; }
      await api.post("/signatures/apply-to-contract", {
        rental_id: rental.id,
        signature: savedSig,
        save_to_client: false,
      });
      toast.success("Firma registrata applicata al contratto!");
      fetchData();
      previewContract(rental.id, rental.contract_number);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSignatureChoice(null);
  };

  const handleDestChoice = (target) => {
    if (!signDestDialog) return;
    const { rental, client } = signDestDialog;
    setSignDestDialog(null);
    if (target === "tablet") {
      const hasSavedSig = client?.has_signature || client?.signatures?.length > 0;
      if (hasSavedSig) {
        setSignatureChoice({ rental, client, vehicle: signDestDialog.vehicle });
      } else {
        sendToKiosk(rental, "tablet");
      }
    } else {
      sendToKiosk(rental, "selfservice");
    }
  };

  const handleSignatureComplete = async (signatureB64) => {
    if (!signatureFlow) return;
    try {
      const api = axiosAuth();
      const saveToClient = settings?.save_tablet_signature || false;
      await api.post("/signatures/apply-to-contract", {
        rental_id: signatureFlow.rental.id,
        signature: signatureB64,
        save_to_client: saveToClient,
      });
      toast.success("Contratto firmato!");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore firma"); }
    setSignatureFlow(null);
  };

  const updatePaymentInline = async (rid, newStatus) => {
    try {
      const api = axiosAuth();
      const res = await api.patch(`/rentals/${rid}/payment`, { payment_status: newStatus });
      setRentals(prev => prev.map(r => r.id === rid ? { ...r, ...res.data } : r));
      toast.success("Pagamento aggiornato");
    } catch { toast.error("Errore aggiornamento"); }
  };

  const [statusConfirm, setStatusConfirm] = useState(null); // { rid, newStatus }

  const updateStatusInline = async (rid, newStatus) => {
    // Intercept "completato" to show completion dialog
    if (newStatus === "completato") {
      const rental = rentals.find(r => r.id === rid);
      if (rental && rental.status === "attivo") {
        const today = new Date().toISOString().split("T")[0];
        const startParts = (rental.start_date || today).split("-").map(Number);
        const todayParts = today.split("-").map(Number);
        const startD = new Date(startParts[0], startParts[1] - 1, startParts[2]);
        const todayD = new Date(todayParts[0], todayParts[1] - 1, todayParts[2]);
        const days = Math.max(1, Math.round((todayD - startD) / 86400000));
        setCompletionForm({ actual_return_date: today, actual_return_time: new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }), actual_days: days });
        setCompletionDialog(rental);
        setCompletionDone(false);
        return;
      }
    }
    // Show confirmation for all other status changes
    setStatusConfirm({ rid, newStatus });
  };

  const confirmStatusChange = async () => {
    if (!statusConfirm) return;
    const { rid, newStatus } = statusConfirm;
    try {
      const api = axiosAuth();
      await api.patch(`/rentals/${rid}/status`, { status: newStatus });
      setRentals(prev => prev.map(r => r.id === rid ? { ...r, status: newStatus } : r));
      toast.success(`Stato: ${statusCfg[newStatus]?.label || newStatus}`);
    } catch { toast.error("Errore aggiornamento"); }
    setStatusConfirm(null);
  };

  const handleCompletion = async () => {
    if (!completionDialog) return;
    setCompletionSubmitting(true);
    try {
      const api = axiosAuth();
      const res = await api.patch(`/rentals/${completionDialog.id}/status`, {
        status: "completato",
        actual_return_date: completionForm.actual_return_date,
        actual_return_time: completionForm.actual_return_time,
        actual_days: completionForm.actual_days
      });
      setRentals(prev => prev.map(r => r.id === completionDialog.id ? { ...r, ...res.data } : r));
      toast.success("Noleggio completato!");
      setCompletionDone(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setCompletionSubmitting(false); }
  };

  const saveDeposit = async (rid) => {
    try {
      const api = axiosAuth();
      const val = parseFloat(depositValue) || 0;
      const res = await api.patch(`/rentals/${rid}/payment`, { deposit_amount: val });
      setRentals(prev => prev.map(r => r.id === rid ? { ...r, ...res.data } : r));
      setEditingDeposit(null);
      toast.success("Acconto aggiornato");
    } catch { toast.error("Errore acconto"); }
  };

  const openEdit = (r) => {
    setEditing(r);
    setOverlapWarning(null);
    setForm({ vehicle_id: r.vehicle_id, client_id: r.client_id, start_date: r.start_date, end_date: r.end_date, start_time: r.start_time || "09:00", end_time: r.end_time || "09:00", daily_rate: r.daily_rate, deposit_amount: r.deposit_amount || 0, payment_status: r.payment_status, status: r.status, notes: r.notes || "", home_delivery: r.home_delivery || false, home_delivery_fee: r.home_delivery_fee ?? 40, extra_day: r.extra_day || false, extra_day_amount: r.extra_day ? (r.extra_day_amount || r.daily_rate || 0) : 0, discount_amount: r.discount_amount || 0, second_driver_id: r.second_driver_id || "", second_driver_fee: r.second_driver_fee || 0 });
    const c = clients.find(x => x.id === r.client_id);
    setClientSearch(c ? `${c.name} ${c.surname}` : "");
    const sd = clients.find(x => x.id === r.second_driver_id);
    setSdSearch(sd ? `${sd.name} ${sd.surname}` : "");
    setDialogOpen(true);
  };

  const openNew = () => { setEditing(null); setOverlapWarning(null); setForm({ ...emptyForm }); setClientSearch(""); setSdSearch(""); setDialogOpen(true); };

  const handleVehicleSelect = (vid) => {
    const v = vehicles.find(x => x.id === vid);
    setForm({ ...form, vehicle_id: vid, daily_rate: v?.daily_rate || form.daily_rate });
  };

  const handleClientSelect = (cid) => {
    const c = clients.find(x => x.id === cid);
    setForm({ ...form, client_id: cid });
    setClientSearch(c ? `${c.name} ${c.surname}` : "");
    setClientDropdownOpen(false);
  };

  const filteredClients = clients.filter(c => {
    const q = clientSearch.toLowerCase();
    return `${c.name} ${c.surname} ${c.address || ""} ${c.phone || ""}`.toLowerCase().includes(q);
  });

  const filteredSdClients = clients.filter(c => {
    const q = sdSearch.toLowerCase();
    return c.id !== form.client_id && `${c.name} ${c.surname} ${c.address || ""} ${c.phone || ""}`.toLowerCase().includes(q);
  });

  const filtered = rentals.filter((r) => {
    const q = search.toLowerCase();
    const ms = `${r.vehicle?.brand || ""} ${r.vehicle?.model || ""} ${r.vehicle?.plate || ""} ${r.client?.name || ""} ${r.client?.surname || ""} ${r.contract_number || ""}`.toLowerCase().includes(q);
    const today = new Date().toISOString().slice(0, 10);
    const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    if (activeTab === "oggi") return ms && (r.start_date?.slice(0, 10) === today || r.end_date?.slice(0, 10) === today) && r.status !== "completato" && r.status !== "annullato";
    if (activeTab === "domani") return ms && (r.start_date?.slice(0, 10) === tomorrow || r.end_date?.slice(0, 10) === tomorrow) && r.status !== "completato" && r.status !== "annullato";
    if (activeTab === "tutti") return ms && (!hideCompleted || (r.status !== "completato" && r.status !== "annullato"));
    if (activeTab === "prenotazioni") return ms && r.status === "prenotazione";
    if (activeTab === "attivi") return ms && r.status === "attivo";
    if (activeTab === "chiusi") return ms && (r.status === "completato" || r.status === "annullato");
    if (activeTab === "da_pagare") return ms && r.status !== "prenotazione" && (r.payment_status === "da_pagare" || r.payment_status === "acconto");
    return ms;
  }).sort((a, b) => {
    const da = `${a.start_date || ""}${a.start_time || "00:00"}`;
    const db2 = `${b.start_date || ""}${b.start_time || "00:00"}`;
    return sortOrder === "desc" ? db2.localeCompare(da) : da.localeCompare(db2);
  });

  return (
    <div className="space-y-6" data-testid="rentals-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-outfit font-bold text-slate-900 dark:text-white">Gestione Noleggi</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{rentals.length} noleggi totali</p>
        </div>
        <Button onClick={openNew} data-testid="add-rental-button" style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg shadow-sm">
          <Plus className="w-4 h-4 mr-2" /> Nuovo Noleggio
        </Button>
      </div>

      {pendingKiosk.length > 0 && (
        <div className="space-y-2" data-testid="pending-kiosk-banner">
          {pendingKiosk.map((p) => (
            <div key={`${p.target}-${p.rental_id}`} className="flex items-center justify-between gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
                <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span></span>
                <span>Firma in corso su <b>{p.target === "tablet" ? "Tablet" : "Kiosk Self-Service"}</b>{p.client ? ` per ${p.client.name || ""} ${p.client.surname || ""}` : ""}{p.vehicle ? ` (${p.vehicle.brand || ""} ${p.vehicle.model || ""})` : ""}</span>
              </div>
              <Button size="sm" variant="outline" onClick={() => cancelPendingKiosk(p.target)} className="h-8 rounded-lg border-amber-400 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 shrink-0" data-testid={`cancel-kiosk-${p.target}`}>
                Annulla
              </Button>
            </div>
          ))}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 dark:bg-slate-800 p-1 rounded-lg" data-testid="rental-tabs">
          <TabsTrigger value="tutti" className="rounded-md text-xs sm:text-sm">Tutti</TabsTrigger>
          <TabsTrigger value="oggi" className="rounded-md text-xs sm:text-sm">Oggi</TabsTrigger>
          <TabsTrigger value="domani" className="rounded-md text-xs sm:text-sm">Domani</TabsTrigger>
          <TabsTrigger value="prenotazioni" className="rounded-md text-xs sm:text-sm">Prenotazioni</TabsTrigger>
          <TabsTrigger value="attivi" className="rounded-md text-xs sm:text-sm">Attivi</TabsTrigger>
          <TabsTrigger value="chiusi" className="rounded-md text-xs sm:text-sm">Chiusi</TabsTrigger>
          <TabsTrigger value="da_pagare" className="rounded-md text-xs sm:text-sm">Da Pagare</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cerca noleggio..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 rounded-lg" data-testid="rentals-search-input" />
            </div>
            <Button variant="outline" size="icon" onClick={() => setSortOrder(s => s === "desc" ? "asc" : "desc")} className="h-10 w-10 rounded-lg shrink-0" data-testid="sort-order-button" title={sortOrder === "desc" ? "Recenti prima" : "Vecchi prima"}>
              <ArrowUpDown className="w-4 h-4" />
            </Button>
            {activeTab === "tutti" && (
              <Button variant={hideCompleted ? "outline" : "default"} onClick={() => setHideCompleted(h => !h)}
                className={`h-10 rounded-lg shrink-0 text-xs px-3 ${!hideCompleted ? "bg-slate-800 text-white hover:bg-slate-700" : ""}`}
                data-testid="hide-completed-button">
                {!hideCompleted ? <Eye className="w-3.5 h-3.5 mr-1.5" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                <span className="hidden sm:inline">{!hideCompleted ? "Nascondi Completati" : "Mostra Completati"}</span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {/* Mobile card view */}
          <div className="md:hidden divide-y divide-slate-200 dark:divide-slate-700">
            {loading ? (
              <p className="text-center py-8 text-slate-400">Caricamento...</p>
            ) : filtered.length === 0 ? (
              <p className="text-center py-8 text-slate-400">Nessun noleggio trovato</p>
            ) : (
              filtered.map((r) => {
                const remaining = r.payment_status === "pagato" ? 0 : Math.max((r.total_amount || 0) - (r.deposit_amount || 0), 0);
                const isExpiredMobile = (r.status === "prenotazione") && r.end_date && r.end_date.slice(0, 10) < new Date().toISOString().slice(0, 10);
                return (
                  <div key={r.id} className={`p-4 space-y-3 ${isExpiredMobile ? "opacity-60" : ""}`} data-testid={`rental-card-${r.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-sm text-slate-900 dark:text-white">{r.vehicle?.brand} {r.vehicle?.model} <span className="font-mono text-xs text-slate-400">{r.vehicle?.plate}</span></p>
                        <p className="text-sm text-slate-600 dark:text-slate-300">{r.client?.name} {r.client?.surname}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        <Badge className={`${(statusCfg[r.status] || statusCfg.prenotazione).cls} border text-[10px]`}>
                          {(statusCfg[r.status] || statusCfg.prenotazione).label}
                        </Badge>
                        {isExpiredMobile && (
                          <Badge className="bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/50 dark:text-orange-300 dark:border-orange-700 border text-[10px]">
                            Scaduta
                          </Badge>
                        )}
                        <Badge className={`${(paymentCfg[r.payment_status] || paymentCfg.da_pagare).cls} border text-[10px]`}>
                          {(paymentCfg[r.payment_status] || paymentCfg.da_pagare).label}
                        </Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div><span className="text-slate-400">Ritiro:</span> <span className="text-slate-700 dark:text-slate-200">{formatDate(r.start_date)} {r.start_time || ""}</span></div>
                      <div><span className="text-slate-400">Riconsegna:</span> <span className="text-slate-700 dark:text-slate-200">{formatDate(r.end_date)} {r.end_time || ""}</span></div>
                      <div><span className="text-slate-400">Totale:</span> <span className="font-semibold text-slate-900 dark:text-white">{r.total_amount?.toFixed(2)} EUR</span></div>
                      <div>
                        <span className="text-slate-400">Acconto: </span>
                        {editingDeposit === r.id ? (
                          <span className="inline-flex items-center gap-1">
                            <input type="text" inputMode="decimal" value={depositValue} onChange={(e) => setDepositValue(e.target.value.replace(",", "."))}
                              onKeyDown={(e) => { if (e.key === "Enter") saveDeposit(r.id); if (e.key === "Escape") setEditingDeposit(null); }}
                              className="w-20 h-6 text-xs px-1 rounded border border-blue-300 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-600 text-right"
                              autoFocus />
                            <button onClick={() => saveDeposit(r.id)} className="text-emerald-600"><Check className="w-3 h-3" /></button>
                            <button onClick={() => setEditingDeposit(null)} className="text-red-500"><X className="w-3 h-3" /></button>
                          </span>
                        ) : (
                          <span className="text-blue-600 font-semibold underline decoration-dotted cursor-pointer"
                            onClick={() => { setEditingDeposit(r.id); setDepositValue(String(r.deposit_amount || 0)); }}>
                            {(r.deposit_amount || 0).toFixed(2)} EUR
                          </span>
                        )}
                      </div>
                      {remaining > 0 && (
                        <div><span className="text-slate-400">Rimanenza:</span> <span className={`font-semibold ${r.payment_status === "acconto" ? "text-orange-500" : "text-red-600"}`}>{remaining.toFixed(2)} EUR</span></div>
                      )}
                      {r.booking_code && (
                        <div><span className="text-slate-400">Cod. Prenot.:</span> <span className="font-mono font-semibold text-blue-600">{r.booking_code}</span></div>
                      )}
                      {r.contract_number && (
                        <div><span className="text-slate-400">Contratto:</span> <span className="font-semibold text-slate-900 dark:text-white">{r.contract_number}</span></div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 pt-1 flex-wrap">
                      {r.status === "attivo" && (
                        <Button onClick={() => {
                          const today = new Date().toISOString().split("T")[0];
                          const startParts = (r.start_date || today).split("-").map(Number);
                          const todayParts = today.split("-").map(Number);
                          const startD = new Date(startParts[0], startParts[1] - 1, startParts[2]);
                          const todayD = new Date(todayParts[0], todayParts[1] - 1, todayParts[2]);
                          const days = Math.max(1, Math.round((todayD - startD) / 86400000));
                          setCompletionForm({ actual_return_date: today, actual_return_time: new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }), actual_days: days });
                          setCompletionDialog(r);
                          setCompletionDone(false);
                        }} size="sm" className="h-8 px-3 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded-md">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Concludi
                        </Button>
                      )}
                      {r.contract_number ? (
                        <>
                          <Button onClick={() => previewContract(r.id, r.contract_number)} size="sm" className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-md">
                            <Eye className="w-3 h-3 mr-1" /> PDF
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => { setTimeDialog({ id: r.id, contract_number: r.contract_number }); setTimeValue(r.start_time || r.contract_time || ""); }} className="h-8 w-8 text-slate-500 hover:text-amber-600">
                            <Clock className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openSignatureFlow(r)} className={`h-8 w-8 ${r.signed ? "text-emerald-500" : "text-violet-500"}`}>
                            <PenTool className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteContract(r.id)} className="h-8 w-8 text-slate-400 hover:text-red-600">
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button onClick={() => createContract(r.id)} disabled={creatingContract === r.id} size="sm" className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md">
                            <FileText className="w-3 h-3 mr-1" /> {creatingContract === r.id ? "..." : "Contratto"}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openSignatureFlow(r)} className={`h-8 w-8 ${r.signed ? "text-emerald-500" : "text-violet-500"}`} title={r.signed ? "Firmato" : "Firma anticipata"}>
                            <PenTool className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                      <div className="ml-auto flex items-center gap-1">
                        {r.pickup_photo && (
                          <Button variant="ghost" size="icon" onClick={() => setPhotoPreview({ rentalId: r.id, photo: r.pickup_photo })} className="h-8 w-8 text-blue-500">
                            <Camera className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => openEdit(r)} className="h-8 w-8 text-slate-500">
                          <Pencil className="w-3 h-3" />
                        </Button>
                        {r.booking_code && (
                          <Button variant="ghost" size="icon" onClick={() => setQrDialog({ booking_code: r.booking_code, rental_id: r.id, client_name: `${r.client?.surname || ""} ${r.client?.name || ""}`.trim(), vehicle_name: `${r.vehicle?.brand || ""} ${r.vehicle?.model || ""}`.trim() })} className="h-8 w-8 text-slate-500 hover:text-blue-600">
                            <QrCode className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => setDeleteDialog(r)} className="h-8 w-8 text-slate-500 hover:text-red-600">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    {(r.notes || r.home_delivery || r.extra_day || r.second_driver) && (
                      <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500 dark:text-slate-400 pt-1">
                        {r.home_delivery && <span className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-md">Domicilio +{(r.home_delivery_fee || 40).toFixed(0)}€</span>}
                        {r.extra_day && <span className="bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded-md">+1 Tariffa {(r.extra_day_amount || r.daily_rate || 0).toFixed(0)}€</span>}
                        {r.discount_amount > 0 && <span className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-md">Sconto -{r.discount_amount.toFixed(0)}€</span>}
                        {r.second_driver && <span className="bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 px-2 py-0.5 rounded-md">2° guid. {r.second_driver.name} {r.second_driver.surname}{r.second_driver_fee > 0 ? ` +${r.second_driver_fee.toFixed(0)}€` : ""}</span>}
                        {r.notes && <span className="italic">{r.notes}</span>}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          {/* Desktop table view */}
          <Table className="hidden md:table">
            <TableHeader>
              <TableRow className="bg-slate-50/50 dark:bg-slate-800/50">
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200">N.</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200">Veicolo</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 hidden md:table-cell">Cliente</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 hidden lg:table-cell">Ritiro</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 hidden lg:table-cell">Riconsegna</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200">Totale</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200">Acconto</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 hidden md:table-cell">Rimanenza</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200">Pagamento</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200">Stato</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={11} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center py-8 text-slate-400">Nessun noleggio trovato</TableCell></TableRow>
              ) : (
                filtered.map((r) => {
                  const remaining = r.payment_status === "pagato" ? 0 : Math.max((r.total_amount || 0) - (r.deposit_amount || 0), 0);
                  const isExpired = (r.status === "prenotazione") && r.end_date && r.end_date.slice(0, 10) < new Date().toISOString().slice(0, 10);
                  return (
                    <TableRow key={r.id} className={`hover:bg-slate-50/50 dark:hover:bg-slate-800/50 ${isExpired ? "opacity-60" : ""}`} data-testid={`rental-row-${r.id}`}>
                      <TableCell className="text-xs font-mono">
                        {r.contract_number ? (
                          <span className="text-slate-900 dark:text-white font-semibold">{r.contract_number}</span>
                        ) : r.booking_code ? (
                          <span className="text-blue-600 font-semibold">{r.booking_code}</span>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600 italic">--</span>
                        )}
                        {r.contract_number && r.booking_code && (
                          <p className="text-[10px] text-blue-500 font-semibold mt-0.5">{r.booking_code}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-slate-900 dark:text-white text-sm">{r.vehicle?.brand} {r.vehicle?.model}</p>
                        <p className="text-[11px] font-mono text-slate-400 dark:text-slate-500">{r.vehicle?.plate}</p>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <p className="text-sm text-slate-700 dark:text-slate-200">{r.client?.name} {r.client?.surname}</p>
                        {r.client?.address && <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate max-w-[150px]">{r.client.address}</p>}
                        {(r.notes || r.home_delivery || r.extra_day || r.second_driver) && (
                          <div className="flex items-center gap-1.5 flex-wrap mt-1">
                            {r.home_delivery && <span className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded text-[10px] leading-tight">Domicilio +{(r.home_delivery_fee || 40).toFixed(0)}€</span>}
                            {r.extra_day && <span className="bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded text-[10px] leading-tight">+1 Tariffa {(r.extra_day_amount || r.daily_rate || 0).toFixed(0)}€</span>}
                            {r.discount_amount > 0 && <span className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded text-[10px] leading-tight">Sconto -{r.discount_amount.toFixed(0)}€</span>}
                            {r.second_driver && <span className="bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 px-1.5 py-0.5 rounded text-[10px] leading-tight">2° guid. {r.second_driver.name} {r.second_driver.surname}{r.second_driver_fee > 0 ? ` +${r.second_driver_fee.toFixed(0)}€` : ""}</span>}
                            {r.notes && <span className="text-[10px] text-amber-600 dark:text-amber-400 italic truncate max-w-[150px]" title={r.notes}>{r.notes}</span>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="text-sm text-slate-700 dark:text-slate-200">{formatDate(r.start_date)}</div>
                        <div className="text-xs text-slate-400">{r.start_time || ""}</div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="text-sm text-slate-700 dark:text-slate-200">{formatDate(r.end_date)}</div>
                        <div className="text-xs text-slate-400">{r.end_time || ""}</div>
                      </TableCell>
                      <TableCell className="font-semibold text-sm text-slate-900 dark:text-white">{r.total_amount?.toFixed(2)} EUR</TableCell>
                      <TableCell>
                        {editingDeposit === r.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="number" step="0.01" value={depositValue}
                              onChange={(e) => setDepositValue(e.target.value)}
                              className="h-7 w-20 text-xs rounded"
                              autoFocus
                              onKeyDown={(e) => { if (e.key === "Enter") saveDeposit(r.id); if (e.key === "Escape") setEditingDeposit(null); }}
                              data-testid={`deposit-input-${r.id}`}
                            />
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => saveDeposit(r.id)}>
                              <Check className="w-3 h-3 text-emerald-600" />
                            </Button>
                          </div>
                        ) : (
                          <button
                            className="text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors"
                            onClick={() => { setEditingDeposit(r.id); setDepositValue(String(r.deposit_amount || 0)); }}
                            data-testid={`deposit-click-${r.id}`}
                            title="Clicca per modificare acconto"
                          >
                            {(r.deposit_amount || 0).toFixed(2)} EUR
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm font-semibold">
                        <span className={remaining > 0 ? (r.payment_status === "acconto" ? "text-orange-500 dark:text-orange-400" : "text-red-600 dark:text-red-400") : "text-emerald-600 dark:text-emerald-400"}>
                          {remaining.toFixed(2)} EUR
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select value={r.payment_status} onValueChange={(v) => updatePaymentInline(r.id, v)}>
                          <SelectTrigger className="h-7 w-[120px] border-0 p-0 shadow-none focus:ring-0" data-testid={`payment-select-${r.id}`}>
                            <Badge className={`${(paymentCfg[r.payment_status] || paymentCfg.da_pagare).cls} border text-xs cursor-pointer`}>
                              {(paymentCfg[r.payment_status] || paymentCfg.da_pagare).label}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="da_pagare">Da Pagare</SelectItem>
                            <SelectItem value="acconto">Acconto</SelectItem>
                            <SelectItem value="pagato">Pagato</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                        <Select value={r.status} onValueChange={(v) => updateStatusInline(r.id, v)}>
                          <SelectTrigger className="h-7 w-[130px] border-0 p-0 shadow-none focus:ring-0" data-testid={`status-select-${r.id}`}>
                            <Badge className={`${(statusCfg[r.status] || statusCfg.prenotazione).cls} border text-xs cursor-pointer`}>
                              {(statusCfg[r.status] || statusCfg.prenotazione).label}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="prenotazione">Prenotazione</SelectItem>
                            <SelectItem value="attivo">Attivo</SelectItem>
                            <SelectItem value="completato">Completato</SelectItem>
                            <SelectItem value="annullato">Annullato</SelectItem>
                          </SelectContent>
                        </Select>
                        {isExpired && (
                          <Badge className="bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/50 dark:text-orange-300 dark:border-orange-700 border text-[10px] px-1.5 shrink-0" data-testid={`expired-badge-${r.id}`}>
                            Scaduta
                          </Badge>
                        )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {r.contract_number ? (
                            <>
                              <Button onClick={() => previewContract(r.id, r.contract_number)} data-testid={`preview-contract-${r.id}`} className="h-7 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-md shadow-sm">
                                <Eye className="w-3 h-3 mr-1" /> PDF
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => { setTimeDialog({ id: r.id, contract_number: r.contract_number }); setTimeValue(r.start_time || r.contract_time || ""); }} data-testid={`edit-time-${r.id}`} className="h-7 w-7 text-slate-500 hover:text-amber-600 dark:text-slate-400 dark:hover:text-amber-400" title="Modifica ora consegna">
                                <Clock className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => openSignatureFlow(r)} data-testid={`sign-contract-${r.id}`} className={`h-7 w-7 ${r.signed ? "text-emerald-500" : "text-violet-500 hover:text-violet-700 dark:hover:text-violet-300"}`} title={r.signed ? "Contratto firmato" : "Firma contratto"}>
                                <PenTool className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => deleteContract(r.id)} data-testid={`delete-contract-${r.id}`} className="h-7 w-7 text-slate-400 hover:text-red-600 dark:hover:text-red-400" title="Elimina contratto">
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button onClick={() => createContract(r.id)} disabled={creatingContract === r.id} data-testid={`create-contract-${r.id}`} className="h-7 px-2.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm">
                                <FileText className="w-3 h-3 mr-1" />
                                {creatingContract === r.id ? "..." : "Contratto"}
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => openSignatureFlow(r)} data-testid={`sign-rental-${r.id}`} className={`h-7 w-7 ${r.signed ? "text-emerald-500" : "text-violet-500 hover:text-violet-700 dark:hover:text-violet-300"}`} title={r.signed ? "Firmato" : "Firma anticipata"}>
                                <PenTool className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`edit-rental-${r.id}`} className="h-7 w-7 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white">
                            <Pencil className="w-3 h-3" />
                          </Button>
                          {r.booking_code && (
                            <Button variant="ghost" size="icon" onClick={() => setQrDialog({ booking_code: r.booking_code, rental_id: r.id, client_name: `${r.client?.surname || ""} ${r.client?.name || ""}`.trim(), vehicle_name: `${r.vehicle?.brand || ""} ${r.vehicle?.model || ""}`.trim() })} data-testid={`qr-rental-${r.id}`} className="h-7 w-7 text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400" title="QR Code prenotazione">
                              <QrCode className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {r.pickup_photo && (
                            <Button variant="ghost" size="icon" onClick={() => setPhotoPreview({ rentalId: r.id, photo: r.pickup_photo })} data-testid={`view-photo-${r.id}`} className="h-7 w-7 text-blue-500 hover:text-blue-700" title="Foto identificativa">
                              <Camera className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => setDeleteDialog(r)} data-testid={`delete-rental-${r.id}`} className="h-7 w-7 text-slate-500 hover:text-red-600 dark:text-slate-400">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg rounded-xl bg-white dark:bg-slate-900" data-testid="rental-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-slate-900 dark:text-white">{editing ? "Modifica Noleggio" : "Nuovo Noleggio"}</DialogTitle>
            <DialogDescription>{editing ? "Modifica i dati" : "Crea un nuovo noleggio"}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
            {overlapWarning && (
              <div className="col-span-2 flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800" data-testid="overlap-warning">
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">Sovrapposizione Rilevata</p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">{overlapWarning}</p>
                  <p className="text-xs text-red-500 dark:text-red-400 mt-1 font-medium">Modifica le date o l'ora per procedere.</p>
                </div>
              </div>
            )}
            {editing && editing.vehicle_id !== form.vehicle_id && (
              <div className="col-span-2 flex items-center gap-2 p-2.5 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <ArrowRightLeft className="w-4 h-4 text-blue-600 shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Veicolo cambiato da <b>{editing.vehicle?.brand} {editing.vehicle?.model}</b> ({editing.vehicle?.plate})
                </p>
              </div>
            )}
            {/* Client search (for new and edit) */}
              <div className="space-y-2 col-span-2" ref={clientRef}>
                <Label className="text-slate-700 dark:text-slate-200">Cliente *</Label>
                <div className="relative">
                  <Input
                    data-testid="rental-client-search"
                    placeholder="Cerca cliente per nome, indirizzo..."
                    value={clientSearch}
                    onChange={(e) => { setClientSearch(e.target.value); setClientDropdownOpen(true); if (!e.target.value) setForm({ ...form, client_id: "" }); }}
                    onFocus={() => setClientDropdownOpen(true)}
                    className="h-10 rounded-lg"
                    autoComplete="off"
                  />
                  {clientDropdownOpen && clientSearch.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredClients.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-slate-400">Nessun cliente trovato</div>
                      ) : filteredClients.map(c => (
                        <button
                          key={c.id}
                          onClick={() => handleClientSelect(c.id)}
                          className={`w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${form.client_id === c.id ? "bg-slate-100 dark:bg-slate-700" : ""}`}
                          data-testid={`client-option-${c.id}`}
                        >
                          <p className="text-sm font-medium text-slate-900 dark:text-white">{c.name} {c.surname}</p>
                          {c.address && <p className="text-[10px] text-slate-400 dark:text-slate-500">{c.address}{c.city ? `, ${c.city}` : ""}</p>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {form.client_id && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400">
                    Selezionato: {clients.find(c => c.id === form.client_id)?.name} {clients.find(c => c.id === form.client_id)?.surname}
                  </p>
                )}
              </div>
            {/* Second driver search */}
              <div className="space-y-2 col-span-2" ref={sdRef}>
                <Label className="text-slate-700 dark:text-slate-200">2° Guidatore (opzionale)</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      data-testid="rental-second-driver-search"
                      placeholder="Cerca secondo guidatore..."
                      value={sdSearch}
                      onChange={(e) => { setSdSearch(e.target.value); setSdDropdownOpen(true); if (!e.target.value) setForm({ ...form, second_driver_id: "" }); }}
                      onFocus={() => setSdDropdownOpen(true)}
                      className="h-10 rounded-lg"
                      autoComplete="off"
                    />
                    {sdDropdownOpen && sdSearch.length > 0 && (
                      <div className="absolute z-50 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredSdClients.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-slate-400">Nessun cliente trovato</div>
                        ) : filteredSdClients.map(c => (
                          <button
                            key={c.id}
                            onClick={() => { setForm({ ...form, second_driver_id: c.id }); setSdSearch(`${c.name} ${c.surname}`); setSdDropdownOpen(false); }}
                            className={`w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${form.second_driver_id === c.id ? "bg-slate-100 dark:bg-slate-700" : ""}`}
                            data-testid={`second-driver-option-${c.id}`}
                          >
                            <p className="text-sm font-medium text-slate-900 dark:text-white">{c.name} {c.surname}</p>
                            {c.license_number && <p className="text-[10px] text-slate-400 dark:text-slate-500">Patente: {c.license_number}</p>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {form.second_driver_id && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Input type="text" inputMode="decimal" title="Costo extra 2° guidatore" placeholder="0"
                        value={form.second_driver_fee}
                        onChange={(e) => { const v = e.target.value.replace(",", "."); setForm({ ...form, second_driver_fee: v === "" || v === "." ? v : (isNaN(Number(v)) ? form.second_driver_fee : v) }); }}
                        onBlur={() => setForm(f => ({ ...f, second_driver_fee: parseFloat(String(f.second_driver_fee).replace(",", ".")) || 0 }))}
                        className="h-10 w-20 text-sm rounded-lg text-right" data-testid="second-driver-fee-input" />
                      <span className="text-xs text-slate-500">EUR</span>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500" title="Rimuovi 2° guidatore"
                        onClick={() => { setForm({ ...form, second_driver_id: "", second_driver_fee: 0 }); setSdSearch(""); }} data-testid="second-driver-remove-btn">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                {form.second_driver_id && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400" data-testid="second-driver-selected">
                    2° guidatore: {clients.find(c => c.id === form.second_driver_id)?.name} {clients.find(c => c.id === form.second_driver_id)?.surname}{(parseFloat(String(form.second_driver_fee).replace(",", ".")) || 0) > 0 ? ` (+${(parseFloat(String(form.second_driver_fee).replace(",", ".")) || 0).toFixed(2)} EUR)` : " (gratuito)"}
                  </p>
                )}
              </div>
            {/* Dates first */}
            <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Data Ritiro *</Label><Input data-testid="rental-start-date-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="h-10 rounded-lg" /></div>
            <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Ora Ritiro</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="h-10 rounded-lg" /></div>
            <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Data Riconsegna *</Label><Input data-testid="rental-end-date-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="h-10 rounded-lg" /></div>
            <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Ora Riconsegna</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="h-10 rounded-lg" /></div>
            {/* Vehicle after dates */}
            <div className="space-y-2 col-span-2">
              <Label className="text-slate-700 dark:text-slate-200">Veicolo *</Label>
              <Select value={form.vehicle_id} onValueChange={handleVehicleSelect}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="rental-vehicle-trigger"><SelectValue placeholder="Seleziona veicolo" /></SelectTrigger>
                <SelectContent>
                  {vehicles.length === 0 ? (
                    <SelectItem value="none" disabled>Nessun veicolo</SelectItem>
                  ) : vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} - {v.plate} ({v.daily_rate} EUR/g)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Tariffa/G (EUR)</Label><Input type="text" inputMode="decimal" value={form.daily_rate} onChange={(e) => { const v = e.target.value.replace(",", "."); setForm({ ...form, daily_rate: v === "" || v === "." ? v : (isNaN(Number(v)) ? form.daily_rate : v) }); }} onBlur={(e) => setForm({ ...form, daily_rate: parseFloat(String(form.daily_rate).replace(",", ".")) || 0 })} className="h-10 rounded-lg" /></div>
            <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Acconto (EUR)</Label><Input type="text" inputMode="decimal" value={form.deposit_amount} onChange={(e) => { const v = e.target.value.replace(",", "."); setForm({ ...form, deposit_amount: v === "" || v === "." ? v : (isNaN(Number(v)) ? form.deposit_amount : v) }); }} onBlur={(e) => setForm({ ...form, deposit_amount: parseFloat(String(form.deposit_amount).replace(",", ".")) || 0 })} className="h-10 rounded-lg" /></div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-200">Pagamento</Label>
              <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                <SelectTrigger className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="da_pagare">Da Pagare</SelectItem><SelectItem value="acconto">Acconto</SelectItem><SelectItem value="pagato">Pagato</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-200">Stato</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="prenotazione">Prenotazione</SelectItem><SelectItem value="attivo">Attivo</SelectItem><SelectItem value="completato">Completato</SelectItem><SelectItem value="annullato">Annullato</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2">
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setForm(f => ({ ...f, home_delivery: !f.home_delivery }))}
                  className={`relative w-10 h-5 rounded-full transition-colors ${form.home_delivery ? "bg-blue-500" : "bg-slate-300 dark:bg-slate-600"}`}>
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.home_delivery ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                </button>
                <Label className="text-slate-700 dark:text-slate-200">Consegna a domicilio</Label>
                {form.home_delivery && (
                  <div className="flex items-center gap-1">
                    <Input type="text" inputMode="decimal" value={form.home_delivery_fee}
                      onChange={(e) => { const v = e.target.value.replace(",", "."); setForm({ ...form, home_delivery_fee: v === "" || v === "." ? v : (isNaN(Number(v)) ? form.home_delivery_fee : v) }); }}
                      onBlur={() => setForm(f => ({ ...f, home_delivery_fee: parseFloat(String(f.home_delivery_fee).replace(",", ".")) || 0 }))}
                      className="h-8 w-20 text-sm rounded-lg text-right" />
                    <span className="text-xs text-slate-500">EUR</span>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2 col-span-2">
              <div className="flex items-center gap-3">
                <button type="button" data-testid="extra-day-toggle" onClick={() => setForm(f => ({ ...f, extra_day: !f.extra_day, extra_day_amount: !f.extra_day ? (parseFloat(String(f.daily_rate).replace(",", ".")) || 0) : 0 }))}
                  className={`relative w-10 h-5 rounded-full transition-colors ${form.extra_day ? "bg-amber-500" : "bg-slate-300 dark:bg-slate-600"}`}>
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.extra_day ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                </button>
                <Label className="text-slate-700 dark:text-slate-200">Giorno extra (solo tariffa)</Label>
                {form.extra_day && (
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">+</span>
                    <Input type="text" inputMode="decimal" value={form.extra_day_amount}
                      onChange={(e) => { const v = e.target.value.replace(",", "."); setForm({ ...form, extra_day_amount: v === "" || v === "." ? v : (isNaN(Number(v)) ? form.extra_day_amount : v) }); }}
                      onBlur={() => setForm(f => ({ ...f, extra_day_amount: parseFloat(String(f.extra_day_amount).replace(",", ".")) || 0 }))}
                      className="h-8 w-20 text-sm rounded-lg text-right border-amber-300 dark:border-amber-700" data-testid="extra-day-amount" />
                    <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">EUR</span>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2 col-span-2">
              <div className="flex items-center gap-3">
                <Label className="text-slate-700 dark:text-slate-200">Sconto</Label>
                <Input type="text" inputMode="decimal" placeholder="0" value={form.discount_amount}
                  onChange={(e) => { const v = e.target.value.replace(",", "."); setForm({ ...form, discount_amount: v === "" || v === "." ? v : (isNaN(Number(v)) ? form.discount_amount : v) }); }}
                  onBlur={() => setForm(f => ({ ...f, discount_amount: parseFloat(String(f.discount_amount).replace(",", ".")) || 0 }))}
                  className="h-9 w-24 text-sm rounded-lg text-right border-emerald-300 dark:border-emerald-800" data-testid="discount-amount-input" />
                <span className="text-xs text-slate-500">EUR</span>
                {(parseFloat(String(form.discount_amount).replace(",", ".")) || 0) > 0 && (
                  <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-testid="discount-applied-label">-{(parseFloat(String(form.discount_amount).replace(",", ".")) || 0).toFixed(2)} EUR dal totale</span>
                )}
              </div>
            </div>
            <div className="space-y-2 col-span-2"><Label className="text-slate-700 dark:text-slate-200">Note</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note..." className="h-10 rounded-lg" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Annulla</Button>
            <Button onClick={handleSave} disabled={submitting || !form.vehicle_id || (!editing && !form.client_id) || !form.start_date || !form.end_date} data-testid="rental-save-button" style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg">
              {submitting ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-md rounded-xl bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle className="font-outfit text-slate-900 dark:text-white">Elimina Noleggio</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare il noleggio{deleteDialog?.contract_number ? ` #${deleteDialog.contract_number}` : ""} di {deleteDialog?.client?.name} {deleteDialog?.client?.surname}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="rounded-lg" data-testid="cancel-delete-rental">Annulla</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-lg" data-testid="confirm-delete-rental">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completion Dialog (Active -> Completed) */}
      <Dialog open={!!completionDialog} onOpenChange={() => { setCompletionDialog(null); setCompletionDone(false); }}>
        <DialogContent className="sm:max-w-md rounded-xl bg-white dark:bg-slate-900" data-testid="completion-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-slate-900 dark:text-white flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Completa Noleggio
            </DialogTitle>
            <DialogDescription>
              {completionDialog?.vehicle?.brand} {completionDialog?.vehicle?.model} - {completionDialog?.client?.name} {completionDialog?.client?.surname}
            </DialogDescription>
          </DialogHeader>
          {!completionDone ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-700 dark:text-slate-200">Data Riconsegna</Label>
                  <Input type="date" value={completionForm.actual_return_date} onChange={(e) => setCompletionForm({ ...completionForm, actual_return_date: e.target.value })} className="h-10 rounded-lg" data-testid="completion-date" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 dark:text-slate-200">Ora Riconsegna</Label>
                  <Input type="time" value={completionForm.actual_return_time} onChange={(e) => setCompletionForm({ ...completionForm, actual_return_time: e.target.value })} className="h-10 rounded-lg" data-testid="completion-time" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="text-slate-700 dark:text-slate-200">Giorni Effettivi</Label>
                  <Input type="number" min="1" value={completionForm.actual_days} onChange={(e) => setCompletionForm({ ...completionForm, actual_days: parseInt(e.target.value) || 1 })} className="h-10 rounded-lg" data-testid="completion-days" />
                  <p className="text-[11px] text-slate-400">Totale aggiornato: {((completionForm.actual_days * (completionDialog?.daily_rate || 0)) + (completionDialog?.extra_day ? (completionDialog?.extra_day_amount || completionDialog?.daily_rate || 0) : 0)).toFixed(2)} EUR</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setCompletionDialog(null); setCompletionDone(false); }} className="rounded-lg">Annulla</Button>
                <Button onClick={handleCompletion} disabled={completionSubmitting} style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg" data-testid="completion-save-button">
                  {completionSubmitting ? "Salvataggio..." : "Completa Noleggio"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800 text-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Noleggio completato con successo!</p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">{completionForm.actual_days} giorni{completionDialog?.extra_day ? " +1 tariffa extra" : ""} - {((completionForm.actual_days * (completionDialog?.daily_rate || 0)) + (completionDialog?.extra_day ? (completionDialog?.extra_day_amount || completionDialog?.daily_rate || 0) : 0)).toFixed(2)} EUR</p>
              </div>
              {completionDialog?.contract_number && (
                <Button onClick={() => { downloadContract(completionDialog.id); }} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="completion-download-pdf">
                  <Download className="w-4 h-4 mr-2" /> Salva PDF Contratto
                </Button>
              )}
              <Button variant="outline" onClick={() => { setCompletionDialog(null); setCompletionDone(false); fetchData(); }} className="w-full rounded-lg">Chiudi</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Signature Destination Dialog */}
      <Dialog open={!!signDestDialog} onOpenChange={() => setSignDestDialog(null)}>
        <DialogContent className="bg-white dark:bg-slate-900 rounded-xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">Dove vuoi far firmare?</DialogTitle>
            <DialogDescription>Scegli il dispositivo per la firma del cliente</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Button onClick={() => handleDestChoice("tablet")} className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg h-14 text-base" data-testid="dest-tablet-btn">
              <PenTool className="w-5 h-5 mr-3" /> Tablet Wacom
            </Button>
            <Button onClick={() => handleDestChoice("selfservice")} className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-lg h-14 text-base" data-testid="dest-kiosk-btn">
              <QrCode className="w-5 h-5 mr-3" /> Kiosk Self-Service
            </Button>
            <Button variant="ghost" onClick={() => setSignDestDialog(null)} className="w-full rounded-lg h-10 text-slate-500">
              Annulla
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Signature Choice Dialog */}
      <Dialog open={!!signatureChoice} onOpenChange={() => setSignatureChoice(null)}>
        <DialogContent className="bg-white dark:bg-slate-900 rounded-xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">Firma Contratto</DialogTitle>
            <DialogDescription>Il cliente ha una firma registrata. Come vuoi firmare?</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Button onClick={useRegisteredSignature} className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg h-12" data-testid="use-saved-sig-btn">
              <PenTool className="w-4 h-4 mr-2" /> Usa Firma Registrata
            </Button>
            <Button variant="outline" onClick={() => { sendToKiosk(signatureChoice.rental, "tablet"); setSignatureChoice(null); }} className="w-full rounded-lg h-12" data-testid="use-tablet-sig-btn">
              <PenTool className="w-4 h-4 mr-2" /> Firma da Tablet
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Already Signed Dialog */}
      <Dialog open={!!signedDialog} onOpenChange={() => setSignedDialog(null)}>
        <DialogContent className="bg-white dark:bg-slate-900 rounded-xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">Contratto Gia Firmato</DialogTitle>
            <DialogDescription>Questo contratto ha gia una firma. Cosa vuoi fare?</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Button onClick={() => resignContract(signedDialog)} className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg h-12" data-testid="resign-contract-btn">
              <PenTool className="w-4 h-4 mr-2" /> Firma di Nuovo
            </Button>
            <Button variant="outline" onClick={() => removeSignature(signedDialog.id)} className="w-full rounded-lg h-12 text-red-600 border-red-200 hover:bg-red-50" data-testid="remove-signature-btn">
              <X className="w-4 h-4 mr-2" /> Elimina Firma
            </Button>
            <Button variant="ghost" onClick={() => setSignedDialog(null)} className="w-full rounded-lg h-10 text-slate-500">
              Annulla
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Status Change Confirmation */}
      <Dialog open={!!statusConfirm} onOpenChange={() => setStatusConfirm(null)}>
        <DialogContent className="bg-white dark:bg-slate-900 rounded-xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">Conferma Cambio Stato</DialogTitle>
            <DialogDescription>
              Vuoi cambiare lo stato in <span className="font-semibold">{statusConfirm ? (statusCfg[statusConfirm.newStatus]?.label || statusConfirm.newStatus) : ""}</span>?
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setStatusConfirm(null)} className="flex-1 rounded-lg h-11">
              Annulla
            </Button>
            <Button onClick={confirmStatusChange} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg h-11" data-testid="confirm-status-change-btn">
              Conferma
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Signature Flow (fullscreen) */}
      {signatureFlow && (
        <SignatureFlow
          rental={signatureFlow.rental}
          client={signatureFlow.client}
          vehicle={signatureFlow.vehicle}
          onComplete={handleSignatureComplete}
          onCancel={() => setSignatureFlow(null)}
          axiosAuth={axiosAuth}
        />
      )}

      {/* PDF Preview Dialog */}
      <Dialog open={!!pdfPreview} onOpenChange={closePdfPreview}>
        <DialogContent className="bg-white dark:bg-slate-900 rounded-xl max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
          <div className="flex items-center justify-center px-5 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0 gap-3">
              <Button onClick={printPreview} variant="outline" size="sm" className="rounded-lg text-xs" data-testid="print-contract-btn">
                <Printer className="w-3.5 h-3.5 mr-1.5" /> Stampa
              </Button>
              <Button onClick={() => downloadContract(pdfPreview?.rid)} size="sm" className="rounded-lg text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="download-contract-btn">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Scarica
              </Button>
          </div>
          <div className="flex-1 min-h-0">
            {pdfPreview?.url && (
              <iframe
                id="pdf-preview-iframe"
                src={pdfPreview.url}
                title="Anteprima PDF"
                className="w-full h-full border-0"
                data-testid="pdf-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Preview Dialog */}
      <Dialog open={!!photoPreview} onOpenChange={() => setPhotoPreview(null)}>
        <DialogContent className="bg-white dark:bg-slate-900 rounded-xl max-w-lg p-0 gap-0">
          <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700">
            <DialogHeader className="p-0 space-y-0">
              <DialogTitle className="text-base font-bold text-slate-900 dark:text-white">Foto Identificativa</DialogTitle>
              <DialogDescription>Foto scattata al ritiro del veicolo</DialogDescription>
            </DialogHeader>
          </div>
          <div className="p-4">
            {photoPreview?.photo && (
              <img src={`data:image/jpeg;base64,${photoPreview.photo}`} alt="Foto identificativa" className="w-full rounded-xl" />
            )}
          </div>
          <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
            <Button variant="outline" onClick={() => deletePickupPhoto(photoPreview?.rentalId)} className="rounded-lg text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-photo-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Elimina Foto
            </Button>
            <Button variant="outline" onClick={() => setPhotoPreview(null)} className="rounded-lg text-xs">Chiudi</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Time Edit Dialog */}
      <Dialog open={!!timeDialog} onOpenChange={() => setTimeDialog(null)}>
        <DialogContent className="max-w-xs rounded-2xl" data-testid="time-dialog">
          <DialogHeader>
            <DialogTitle>Modifica Ora Consegna</DialogTitle>
            <DialogDescription>Contratto {timeDialog?.contract_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Input
              type="time"
              value={timeValue}
              onChange={(e) => setTimeValue(e.target.value)}
              className="h-12 text-center text-2xl rounded-lg"
              data-testid="time-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setTimeDialog(null)} className="flex-1 rounded-lg">Annulla</Button>
              <Button onClick={async () => {
                const id = timeDialog?.id;
                if (!id) return;
                try {
                  const api = axiosAuth();
                  await api.patch(`/rentals/${id}/contract-time`, { contract_time: timeValue });
                  toast.success("Ora contratto aggiornata");
                  setTimeDialog(null);
                  fetchRentals();
                } catch (err) { toast.error(err.response?.data?.detail || "Errore aggiornamento ora"); }
              }} className="flex-1 rounded-lg" style={{ backgroundColor: accent }}>
                <span className="text-white">Salva</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={!!qrDialog} onOpenChange={() => setQrDialog(null)}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle className="text-center">QR Code Prenotazione</DialogTitle>
          </DialogHeader>
          {qrDialog && (
            <div className="flex flex-col items-center space-y-4 py-4">
              <QRCodeSVG
                value={qrDialog.booking_code}
                size={224}
                level="M"
                className="mx-auto"
              />
              <p className="text-2xl font-bold font-mono tracking-widest text-slate-900 dark:text-white">{qrDialog.booking_code}</p>
              <div className="text-center text-sm text-slate-500">
                <p>{qrDialog.client_name}</p>
                <p>{qrDialog.vehicle_name}</p>
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg" onClick={() => {
                  const svg = document.querySelector('[data-testid="qr-dialog"] svg');
                  if (!svg) return;
                  const svgData = new XMLSerializer().serializeToString(svg);
                  const canvas = document.createElement("canvas");
                  canvas.width = 400; canvas.height = 400;
                  const ctx = canvas.getContext("2d");
                  const img = new Image();
                  img.onload = () => {
                    ctx.fillStyle = "white";
                    ctx.fillRect(0, 0, 400, 400);
                    ctx.drawImage(img, 0, 0, 400, 400);
                    const a = document.createElement("a");
                    a.download = `QR_${qrDialog.booking_code}.png`;
                    a.href = canvas.toDataURL("image/png");
                    a.click();
                    toast.success("QR scaricato");
                  };
                  img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
                }}>
                  <Download className="w-4 h-4 mr-1.5" /> QR
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" onClick={async () => {
                  try {
                    const api = axiosAuth();
                    const res = await api.get(`/kiosk/booking-summary/${qrDialog.rental_id}`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `Prenotazione_${qrDialog.booking_code}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Riepilogo scaricato");
                  } catch { toast.error("Errore download riepilogo"); }
                }}>
                  <FileText className="w-4 h-4 mr-1.5" /> Riepilogo
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => {
                  const w = window.open("", "_blank");
                  w.document.write(`<html><head><title>QR ${qrDialog.booking_code}</title><style>body{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:sans-serif;margin:0}img{width:300px}h1{font-size:48px;letter-spacing:8px;font-family:monospace;margin:20px 0 5px}p{color:#666;margin:4px}</style></head><body><img src="${process.env.REACT_APP_BACKEND_URL}/api/kiosk/qrcode/${qrDialog.booking_code}"/><h1>${qrDialog.booking_code}</h1><p>${qrDialog.client_name}</p><p>${qrDialog.vehicle_name}</p><script>setTimeout(()=>window.print(),500)</script></body></html>`);
                  w.document.close();
                }}>
                  <Printer className="w-4 h-4 mr-1.5" /> Stampa QR
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
